/**
 * Copyright 2021 Felix Martinez
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software
 * and associated documentation files (the "Software"),to deal in the Software without restriction,
 * including without limitation the rights to use, copy, modify, merge, publish, distribute,
 * sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or
 * substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
 * IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN
 * ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import {
  InstancedBufferAttribute,
  BufferGeometry,
  InstancedMesh,
  Object3D,
  SphereBufferGeometry,
  Vector4,
  Clock,
  MeshLambertMaterial,
  ShaderMaterial,
  ShaderLib,
  MeshDepthMaterial,
  RawShaderMaterial,
  TextureLoader,
  MeshPhongMaterial,
} from 'three'

import type GPUSimulation from './GPUSimulation'
import { fsParameters, fsTransformColor, vsParameters, vsTransformVertex } from '../../shaders/chunks/gridChunks'
import { customDepthFragment, customDepthVertex } from '../../shaders/chunks/depthChunks'

class GPURendering extends Object3D {
  private mesh?: InstancedMesh<BufferGeometry, any>

  constructor(
    private gpuSimulation: GPUSimulation,
    private clock: Clock,
    private geometry: BufferGeometry | null = null,
    private colorPaletteCoord: number = (window as any).fxrand()
  ) {
    super()
    this.setup()
  }

  public update() {
    this.mesh!.material.userData.uniforms.uPositionsTexture.value = this.gpuSimulation.currentPositionRendertarget!.texture
    this.mesh!.material.userData.uniforms.uTime.value = this.clock.getElapsedTime()
  }

  private setup(): void {
    const tl = new TextureLoader()
    const uniforms = {
      uPositionsTexture: {
        value: this.gpuSimulation.currentPositionRendertarget!.texture,
      },
      uTime: {
        value: (window as any).fxrand(),
      },
      colorPalette: {
        value: tl.load('./assets/color_palette.png'),
      },
      uRandomColor: {
        value: this.colorPaletteCoord,
      },
    }

    const geo: BufferGeometry = this.geometry || new SphereBufferGeometry(1)
    
    const material = new MeshPhongMaterial({
      flatShading: true,
    })
    material.userData.uniforms = uniforms
    material.onBeforeCompile = shader => {
      shader.uniforms.uPositionsTexture = material.userData.uniforms.uPositionsTexture
      shader.uniforms.uRandomColor = material.userData.uniforms.uRandomColor
      shader.uniforms.colorPalette = material.userData.uniforms.colorPalette
      shader.uniforms.uTime = material.userData.uniforms.uTime

      shader.vertexShader = shader.vertexShader.replace('#include <common>', vsParameters)
      shader.vertexShader = shader.vertexShader.replace('#include <begin_vertex>', vsTransformVertex)

      shader.fragmentShader = shader.fragmentShader.replace('#include <common>', fsParameters)
      shader.fragmentShader = shader.fragmentShader.replace('#include <color_fragment>', fsTransformColor)
    }

    const i2uvBuffer: InstancedBufferAttribute = new InstancedBufferAttribute(
      new Float32Array(this.gpuSimulation.particleCount * 2),
      2
    )

    const td = this.gpuSimulation.textureDimensions!
    for (let i = 0; i < this.gpuSimulation.particleCount; i++) {
      i2uvBuffer.setXY(i, (i % td[0]) / td[0], Math.floor(i / td[0]) / td[1])
    }

    geo.setAttribute('i2uv', i2uvBuffer)
    this.mesh = new InstancedMesh(geo, material, this.gpuSimulation.particleCount)
    this.mesh.customDepthMaterial = new ShaderMaterial( {
      defines: {
        'USE_SHADOWMAP': '',
        'DEPTH_PACKING': '3201'
      },
      uniforms: material.userData.uniforms,
      vertexShader: customDepthVertex,
      fragmentShader: customDepthFragment,
    } );

    this.mesh.frustumCulled = false
    this.mesh.castShadow = true
    this.mesh.receiveShadow = true
    
    for(let i = 0; i < this.gpuSimulation.particleCount; i++) this.mesh.setMatrixAt(i, this.mesh.matrix)

    this.add(this.mesh)
  }
}

export default GPURendering
